import type { NoteSummary } from '@ofmos/shared/annotations';
import type { ReactNode } from 'react';
import type { FieldMetadata, RecordEvent } from '../../../data/client';
import { formatRelative } from '../../../fields/format';
import { ExpandableText, Icon, type IconName } from '../../../ui';
import { CORRECTION_CONTEXT, correctionSentence, eventDay, noteParts } from './activity';

/**
 * ONE ACTIVITY ENTRY IS ONE LIST ROW (redesign register A2, D7, L13; record 15; brief
 * FE-FOUNDATION section 14), in `app/record/StreamRow.tsx`'s construction: a glyph in the
 * kit's 24 slot, the title at 15, one secondary line at 13, the time at the trailing edge at
 * 13, no connecting line between rows (L13). Consumed by the client page tomorrow.
 *
 * `CorrectionRow` draws A2's sentence for a correction event and returns null for any other
 * event, which the page then shows as the existing timeline row. `NoteRow` draws a note as the
 * note's first line, its author and day, and the rest under "More" (D7 "notes appear in
 * Activity").
 */
export function ActivityRow({
  glyph,
  mark,
  title,
  secondary,
  time,
  children,
}: {
  /** A symbol of the product's icon set in the 24 slot. */
  readonly glyph?: IconName | undefined;
  /**
   * Or a mark the caller draws in the same slot (BR-CLIENTPAGE): a mono object glyph or a status
   * symbol, as the client page's Activity draws them (register A7 12.6 A, mono symbols).
   */
  readonly mark?: ReactNode | undefined;
  readonly title: ReactNode;
  readonly secondary?: ReactNode | undefined;
  readonly time?: ReactNode | undefined;
  readonly children?: ReactNode | undefined;
}) {
  return (
    <li className="flex items-start gap-3 py-2" data-activity-row="">
      <span className="flex size-(--record-glyph-slot) shrink-0 items-center justify-center text-ink-muted">
        {mark ?? (glyph === undefined ? null : <Icon name={glyph} size="1rem" />)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-3">
          <p className="min-w-0 flex-1 text-sm text-ink">{title}</p>
          {time !== undefined && <span className="shrink-0 text-2xs text-ink-muted">{time}</span>}
        </div>
        {secondary !== undefined && <p className="text-2xs text-ink-muted">{secondary}</p>}
        {children}
      </div>
    </li>
  );
}

export function CorrectionRow({
  event,
  fields,
  relationLabels,
}: {
  readonly event: RecordEvent;
  readonly fields: readonly FieldMetadata[];
  readonly relationLabels?: Readonly<Record<string, string>> | undefined;
}) {
  const sentence = correctionSentence(event, fields, relationLabels);
  if (sentence === null) return null;
  return (
    <ActivityRow
      glyph="pencil"
      title={sentence}
      secondary={event.actor.context ?? CORRECTION_CONTEXT}
    />
  );
}

export function NoteRow({ note, now }: { readonly note: NoteSummary; readonly now?: Date }) {
  const parts = noteParts(note.body);
  return (
    <ActivityRow
      glyph="info"
      title={parts.title}
      secondary={note.createdByName ?? undefined}
      time={
        <time dateTime={note.createdAt} title={eventDay(note.createdAt)}>
          {formatRelative(note.createdAt, now)}
        </time>
      }
    >
      {parts.rest !== null && (
        <ExpandableText
          lines={2}
          subject={parts.title}
          className="text-2xs text-ink-muted whitespace-pre-line"
        >
          {parts.rest}
        </ExpandableText>
      )}
    </ActivityRow>
  );
}
